/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Button,
  Col,
  Modal,
  notification,
  Row,
  Space,
  TablePaginationConfig,
  TableProps,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { UploadOutlined, DeleteFilled } from '@ant-design/icons';
import TableComponent from '@/components/table';
import { IImportCoa, ISearchCoaReq } from '@/interfaces/stock';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { getExcelData } from '@/helpers/excelHelper';
import { useMutation } from '@tanstack/react-query';
import { importStockCoa } from '@/adapters/stock';
import { twMerge } from 'tailwind-merge';
import { isEmpty, isUndefined } from 'lodash';

interface IProps {
  open: boolean;
  handleOk: () => void;
  handleCancel: () => void;
}

const _criteria: ISearchCoaReq = {
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
};

const ImportCoaModal = ({ open, handleOk, handleCancel }: IProps) => {
  const [criteria, setCriteria] = useState<ISearchCoaReq>({
    ..._criteria,
  });
  const [coa, setCoa] = useState<IImportCoa[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IImportCoa> | SorterResult<IImportCoa>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof IImportCoa) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const columns: TableProps<IImportCoa>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      align: 'center',
      className: 'text-center',
      render: (_, rec, i) => <Space>{i + 1}</Space>,
    },
    {
      title: 'Lot no',
      dataIndex: 'lotNo',
      key: 'lotNo',
      className: 'font-bold',
      width: 120,
      align: 'center',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      className: 'font-bold',
      width: 120,
      align: 'center',
    },
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      className: 'font-bold',
      width: 120,
      align: 'center',
    },
    {
      title: 'Mime Type',
      dataIndex: 'mimeType',
      key: 'mimeType',
      className: 'font-bold',
      width: 120,
      align: 'center',
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      fixed: 'right',
      align: 'center',
      width: 80,
      className: 'font-bold',
      render: _ => (
        <Space
          className={twMerge(
            _ === 2 && 'text-red-600',
            _ === 3 && 'text-green-700',
          )}
        >
          {_ === 1 ? 'Pending' : _ === 2 ? 'Error' : 'Success'}
        </Space>
      ),
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      align: 'center',
      width: 40,
      className: 'font-bold',
      render: (_, rec) => (
        <DeleteFilled
          className="text-red-700"
          onClick={() => setCoa(c => c.filter(x => x.lotNo !== rec.lotNo))}
        />
      ),
    },
  ];

  const props: UploadProps = {
    onRemove: () => {
      setFileList([]);
      setCoa([]);
    },
    beforeUpload: async file => {
      setFileList([file]);
      let excelData: any[] = (await getExcelData(file)) as any[];
      excelData = excelData
        .slice(1)
        .filter(c => !isEmpty(c[0]) || !isUndefined(c[0]))
        .map((c: any) => {
          const coa: IImportCoa = {
            fileName: c[1].toString(),
            lotNo: c[0].toString(),
            mimeType: c[3].toString(),
            name: c[2].toString(),
            status: 1,
          };
          return coa;
        });
      setCoa(excelData);
    },
    fileList,
    showUploadList: true,
    accept: '.xlsx',
  };

  const mutations = useMutation({
    mutationFn: async () => await importStockCoa(coa),
    mutationKey: ['import-coa'],
    onSuccess: data => {
      if (data.status === 200) {
        setFileList([]);
        setCoa([]);
        notification.success({
          message: 'นำเข้า COA สำเร็จ',
        });
        handleOk();
      } else {
        const duplicateCoas: string[] = data.data?.data;
        const updateCoa = coa.map(c => {
          c.status = duplicateCoas.includes(c.lotNo ?? '') ? 2 : 3;
          return c;
        });
        setCoa(updateCoa);
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const onImportData = () => {
    mutations.mutateAsync();
  };

  return (
    <Modal
      title="นำเข้า COA"
      open={open}
      width={'1200px'}
      onOk={() => handleOk}
      onCancel={handleCancel}
      okText="บันทึก"
      cancelText="ยกเลิก"
      footer={[
        <Button key="back" onClick={handleCancel}>
          ปิด
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="unit"
          className=" !hover:"
          onClick={() => onImportData()}
          loading={mutations.isPending}
        >
          นำเข้า
        </Button>,
      ]}
    >
      <div className="flex flex-col gap-2 w-full">
        <Row gutter={12}>
          <Col span={24} md={8}>
            <Upload {...props}>
              {fileList.length === 0 && (
                <Button icon={<UploadOutlined />}>เลือกไฟล์</Button>
              )}
            </Upload>
          </Col>
        </Row>
        <TableComponent<IImportCoa>
          columns={columns}
          criteria={criteria}
          data={{
            data: coa,
            totalRecords: coa.length,
          }}
          loading={false}
          onTableChange={onTableChange}
          scroll
        />
      </div>
    </Modal>
  );
};

export default ImportCoaModal;
